<template>
    <CContainer class="section" fluid>
        <CRow class="border-bottom">
            <CCol md="10">
                <h2 class="section__title my-4">{{ $t("account_manager") }}</h2>
            </CCol>
            <CCol md="2">
                <CButton
                    color="primary"
                    class="button my-4 shadow"
                    size="sm"
                    @click="modals.createUser.show = true"
                >
                    <span class="button__icon"
                        ><CIcon size="sm" :content="$options.icons.cilSave"
                    /></span>
                    <span class="button__text">{{ $t("add_user") }}</span>
                </CButton>
            </CCol>
        </CRow>
        <CRow alignVertical="center" alignHorizontal="center">
            <CCol sm="12">
                <h3 class="mt-3">{{ $t("list_users") }}</h3>
            </CCol>
            <CCol md="10" sm="12">
                <CDataTable
                    :addTableClasses="['table--sm']"
                    :items="tables.users.items"
                    :fields="
                        traslateArrayObjects({
                            array: tables.users.fields,
                            fields: ['label'],
                        })
                    "
                    :columnFilterValue.sync="tables.users.columnFilterValue"
                    @update:column-filter-value="loadTableUsers()"
                    :items-per-page="tables.users.rows"
                    v-bind="tables.users"
                >
                    <template #over-table>
                        <CContainer :fluid="true">
                            <CRow alignHorizontal="start">
                                <CDropdown
                                    color="primary"
                                    :toggler-text="$t('actions')"
                                    class="m-2"
                                    :disabled="
                                        tables.users.itemSelected === null
                                    "
                                >
                                    <CDropdownItem
                                        tag="div"
                                        @click="
                                            openModalActiveUser(
                                                tables.users.itemSelected
                                            )
                                        "
                                        v-if="tables.users.itemSelected"
                                        :disabled="
                                            tables.users.itemSelected.user_status.toLowerCase() ===
                                            'confirmed'
                                        "
                                    >
                                        <strong>{{ $t("verify") }}</strong>
                                    </CDropdownItem>
                                    <CDropdownItem
                                        tag="div"
                                        v-if="
                                            tables.users.itemSelected && 
                                            !tables.users.itemSelected.enabled
                                        "
                                    >
                                        <strong>{{
                                            $t("active_access_user")
                                        }}</strong>
                                    </CDropdownItem>
                                    <CDropdownItem
                                        tag="div"
                                        v-if="
                                            tables.users.itemSelected &&
                                            tables.users.itemSelected.enabled
                                        "
                                    >
                                        <strong>{{
                                            $t("disable_access_user")
                                        }}</strong>
                                    </CDropdownItem>

                                    <CDropdownItem
                                        tag="div"
                                        @click="
                                            openModalAddUserGroup(
                                                tables.users.itemSelected
                                            )
                                        "
                                    >
                                        <strong>{{ $t("add_group") }}</strong>
                                    </CDropdownItem>
                                </CDropdown>
                            </CRow>
                        </CContainer>
                    </template>
                    <template #_selected="{ item }">
                        <td class="py-2">
                            <CInputRadio
                                name="_selected"
                                :checked="item === tables.users.itemSelected"
                                @update:checked="selectItemTable('users', item)"
                            />
                        </td>
                    </template>
                    <template #user_status="{ item }">
                        <td>
                            <CBadge
                                v-if="
                                    item.user_status.toLowerCase() ===
                                    'confirmed'
                                "
                                color="success"
                                class="mr-2"
                            >
                                {{ $t("confirmed") }}
                            </CBadge>
                            <CBadge v-else color="secondary" class="mr-2">
                                {{ $t("not_confirmed") }}
                            </CBadge>
                        </td>
                    </template>
                    <template #enabled="{ item }">
                        <td>
                            <CBadge
                                v-if="item.enabled"
                                color="success"
                                class="mr-2"
                            >
                                {{ $t("enabled") }}
                            </CBadge>
                            <CBadge v-else color="secondary" class="mr-2">
                                {{ $t("disabled") }}
                            </CBadge>
                        </td>
                    </template>
                    <template #groups="{ item }">
                        <td>
                            {{ item.groups.join(", ") }}
                        </td>
                    </template>
                    <template #actions="{ item }">
                        <td>
                            <CDropdown
                                color="primary"
                                :toggler-text="$t('actions')"
                                class="m-2"
                            >
                                <CDropdownItem
                                    tag="div"
                                    class="text-cent\er"
                                    @click="openModalActiveUser(item)"
                                    :disabled="
                                        item.user_status.toLowerCase() ===
                                        'confirmed'
                                    "
                                >
                                    <strong>{{ $t("verify") }}</strong>
                                </CDropdownItem>
                                <CDropdownItem
                                    tag="div"
                                    class="text-cent\er"
                                    v-if="!item.enabled"
                                >
                                    <strong>{{
                                        $t("active_access_user")
                                    }}</strong>
                                </CDropdownItem>
                                <CDropdownItem
                                    tag="div"
                                    class="text-cent\er"
                                    v-if="item.enabled"
                                >
                                    <strong>{{
                                        $t("disable_access_user")
                                    }}</strong>
                                </CDropdownItem>

                                <CDropdownItem
                                    tag="div"
                                    class="text-cent\er"
                                    @click="openModalAddUserGroup(item)"
                                >
                                    <strong>{{ $t("add_group") }}</strong>
                                </CDropdownItem>
                            </CDropdown>
                        </td>
                    </template>
                </CDataTable>
                <CPagination
                    v-if="tables.users.total_pages > 0"
                    @update:activePage="updatePagination"
                    :activePage="tables.users.page"
                    :pages="tables.users.total_pages"
                    align="center"
                />
            </CCol>
        </CRow>
        <CreateUserModal
            :show.sync="modals.createUser.show"
            :title="$t('add_user')"
            v-on:submit="afterCreateUser"
        />
        <ActiveUserModal
            :show.sync="modals.activeUser.show"
            :title="$t('activate_user')"
            :email="modals.activeUser.data.email"
            v-on:submit="afterActiveUser"
        />
        <AddUserGroupModal
            :show.sync="modals.addUserGroup.show"
            :title="$t('add_user_group')"
            :email="modals.addUserGroup.data.email"
            v-on:submit="afterAddUserGroup"
        />
    </CContainer>
</template>

<script>
import { cilPencil, cilCaretBottom, cilCaretTop, cilSave } from "@coreui/icons";
import traslateMixin from "@/mixins/traslate.js";
import tableMixin from "@/mixins/table.js";
import CreateUserModal from "@/pages/user/components/CreateUserModal.vue";
import ActiveUserModal from "@/pages/user/components/ActiveUserModal.vue";
import AddUserGroupModal from "@/pages/user/components/AddUserGroupModal.vue";
export default {
    name: "UsersManagerTab",
    mixins: [traslateMixin, tableMixin],
    icons: {
        cilPencil,
        cilCaretBottom,
        cilCaretTop,
        cilSave,
    },

    components: {
        CreateUserModal,
        ActiveUserModal,
        AddUserGroupModal,
    },

    data() {
        return {
            tables: {
                users: {
                    fields: [
                        {
                            key: "_selected",
                            label: "",
                            sortable: false,
                            active: true,
                            fixed: true,
                            filter: false,
                            _filter: false,
                        },
                        {
                            key: "email",
                            label: "email",
                            sorter: false,
                            filter: true,
                            _style: "width:20%",
                        },
                        {
                            key: "name",
                            label: "first_name",
                            sorter: false,
                            filter: false,
                            _style: "width:20%",
                        },
                        {
                            key: "custom:last_name",
                            label: "last_name",
                            sorter: false,
                            filter: false,
                            _style: "width:20%",
                        },
                        {
                            key: "user_status",
                            label: "user_status",
                            sorter: false,
                            filter: false,
                            _style: "width:10%",
                        },
                        {
                            key: "enabled",
                            label: "enabled",
                            sorter: false,
                            filter: false,
                            _style: "width:10%",
                        },
                        {
                            key: "groups",
                            label: "groups",
                            sorter: false,
                            filter: false,
                            _style: "width:10%",
                        },
                        {
                            key: "actions",
                            label: "actions",
                            sorter: false,
                            filter: false,
                            _style: "width:10%",
                        },
                    ],
                    columnFilter: {
                        external: true,
                        lazy: true,
                    },
                    items: [],
                    loading: false,
                    rows: 5,
                    responsive: false,
                    page: 1,
                    total_pages: 0,
                    columnFilterValue: {},
                    itemsPerPageSelect: true,
                    itemSelected: null,
                },
            },
            modals: {
                createUser: {
                    show: false,
                    color: "primary",
                    size: "lg",
                },
                activeUser: {
                    show: false,
                    color: "primary",
                    size: "md",
                    data: {
                        email: null,
                    },
                },
                addUserGroup: {
                    show: false,
                    color: "primary",
                    size: "md",
                    data: {
                        email: null,
                    },
                },
            },
        };
    },
    methods: {
        updatePagination(newValue) {
            this.tables.users.page = newValue;
            this.loadTableUsers();
        },
        openModalActiveUser(user) {
            this.modals.activeUser.data.email = user.email;
            this.modals.activeUser.show = true;
        },
        openModalAddUserGroup(user) {
            this.modals.addUserGroup.data.email = user.email;
            this.modals.addUserGroup.show = true;
        },
        loadTableUsers() {
            this.updateTable({
                tableName: "users",
                url: "/account.api/users",
                adapterFilters: (tableFilters) => {
                    delete tableFilters.where.created_at;
                    return tableFilters;
                },
            });
        },
        afterCreateUser(response) {
            this.modals.createUser.show = false;
            this.loadTableUsers();
        },
        afterActiveUser(response) {
            this.modals.activeUser.show = false;
            this.loadTableUsers();
        },
        afterAddUserGroup(response) {
            this.modals.addUserGroup.show = false;
            this.loadTableUsers();
        },
    },
    mounted() {
        this.loadTableUsers();
    },
};
</script>

<style>
</style>